import React, { useState } from 'react'
import classNames from 'classnames'
import { IconInfoCircle } from '@tabler/icons-react'

import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'

const HUE_COUNT = 4

const OPPORTUNITY_TOOLTIP =
  'A conversion is a desired business outcome that Balto identified on your calls. ' +
  'Conversion opportunities are calls where that outcome was possible.'
// The inline % is always rendered; whether it's visible is decided in CSS by the
// segment's own width (a container query), because "is there room for this text"
// is a question about pixels, not about share. A share threshold got this wrong
// in both directions: a 4% segment has ~56px on a wide screen — plenty — and
// ~24px on a narrow one. Narrow segments still name themselves on hover.

// How a run's calls split across conversion opportunities. Each segment is one
// opportunity, sized proportionally by flex-grow; the % is the share of calls
// that were a *chance* at that outcome. Hover names the opportunity; clicking
// jumps to its card.
//
// NB: the <button> is the DIRECT flex child of the track — it is NOT wrapped in
// a tooltip/span, because a wrapper would become the flex item and the
// proportions would collapse to equal widths. Hover is therefore a small
// cursor-following tooltip (like the prototype) rather than InteractiveTooltip.
export const OverviewBar = ({ opportunities, noOpportunity, onSelect, onSelectNoOpp }) => {
  const [tip, setTip] = useState(null)
  const showTipAt = (text) => (event) => setTip({ text, x: event.clientX, y: event.clientY })
  const noOppShare = Math.round(noOpportunity?.volumeSharePct ?? 0)

  return (
    <div className="overview-bar">
      <div className="overview-bar__label">
        Your calls by conversion opportunity
        <InteractiveTooltip content={OPPORTUNITY_TOOLTIP}>
          <IconInfoCircle className="overview-bar__info icon-svg" />
        </InteractiveTooltip>
      </div>
      <div className="overview-bar__track">
        {opportunities.map((opportunity, index) => {
          const share = Math.round(opportunity.volumeSharePct)
          const tipText = `${opportunity.label || opportunity.name} · present on ${share}% of your calls`
          const showTip = showTipAt(tipText)
          return (
            <button
              key={opportunity.name}
              type="button"
              aria-label={tipText}
              className={classNames(
                'overview-bar__seg',
                `overview-bar__seg--hue-${index % HUE_COUNT}`
              )}
              style={{ flexGrow: opportunity.volumeSharePct }}
              onMouseEnter={showTip}
              onMouseMove={showTip}
              onMouseLeave={() => setTip(null)}
              onClick={() => onSelect(opportunity.name)}
            >
              <span className="overview-bar__seg-label">{share}%</span>
            </button>
          )
        })}
        {noOppShare > 0 && (
          <button
            type="button"
            aria-label={`No conversion opportunity · ${noOppShare}% of your calls`}
            className="overview-bar__seg overview-bar__seg--noopp"
            style={{ flexGrow: noOpportunity.volumeSharePct }}
            onMouseEnter={showTipAt(`No conversion opportunity · ${noOppShare}% of your calls`)}
            onMouseMove={showTipAt(`No conversion opportunity · ${noOppShare}% of your calls`)}
            onMouseLeave={() => setTip(null)}
            onClick={onSelectNoOpp}
          >
            <span className="overview-bar__seg-label">{noOppShare}%</span>
          </button>
        )}
      </div>
      {tip && (
        <div className="overview-bar__tip" style={{ left: tip.x, top: tip.y - 36 }}>
          {tip.text}
        </div>
      )}
    </div>
  )
}
